import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Autocomplete,
    TextField,
    CircularProgress,
    Button,
    IconButton,
    Tooltip,
} from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import StarIcon from '@mui/icons-material/Star';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DocCardImage from '../DocCardImage/DocCardImage';
import { getMachinesWithModels } from "../../services/myMachineService";
import DocumentViewModal from '../DocumentViewModal';
import './DocumentationSidebar.css';
import { themeColors } from '../../../src/utilities/themeColors';
import { getRelativeTime } from '../../utilities/dateUtils';
import { removeFavorite } from '../../services/documentService';
import { trackEvent } from '../../utilities/analyticsService';
const DocumentationSidebar = ({
    selectedMachine,
    onMachineChange,
    favorites = [],
    recentDocuments = [],
    documents = [],
    showOnlyFavorites = false,
    showOnlyRecent = false,
    onToggleFavorites,
    selectedSiteNumber,
    selectedSiteId,
    onToggleRecent,
    favoriteDocumentsWithTimestamps = [],
    onFavoriteRemoved,
    onFileClick,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const pagePathRef = useRef(`${location.pathname}${location.search || ''}`);
    const lastTrackedMachineSearchRef = useRef("");
    // Machine state for infinite scroll + search
    const [machines, setMachines] = useState([]);
    const [machineSearch, setMachineSearch] = useState('');
    const [machineInputValue, setMachineInputValue] = useState('');
    const [machinePage, setMachinePage] = useState(1);
    const [machineHasMore, setMachineHasMore] = useState(true);
    const [machinesLoading, setMachinesLoading] = useState(false);
    const [machineListError, setMachineListError] = useState(false);
    const debounceTimerRef = useRef(null);
    const machineSearchRequestIdRef = useRef(0);
    const activeMachineListRequestRef = useRef(null);
    const highlightedMachineOptionRef = useRef(null);
    const previousSiteNumberRef = useRef(selectedSiteNumber);
    const inFlightMachineRequestsRef = useRef(new Set());
    const completedMachineRequestsRef = useRef(new Set());

    useEffect(() => {
        pagePathRef.current = `${location.pathname}${location.search || ''}`;
    }, [location.pathname, location.search]);

    // Modal state for historical content files
    const [selectedFilePath, setSelectedFilePath] = useState(null);
    const [selectedFileName, setSelectedFileName] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    // All machines option
    const allMachinesOption = useMemo(() => ({
        machine_id: 'ALL',
        machine_name: t('LABELS.ALL_MACHINES'),
        machine_number: '',
    }), [t]);

    const getMachineDisplayName = useCallback((machine) => {
        if (!machine) return '';
        if (machine.disabled) return machine.machine_name || '';
        if (machine.machine_id === 'ALL') return t('LABELS.ALL_MACHINES');
        if (!machine.machine_name && !machine.machine_number) return String(machine.machine_id || '');
        if (machine.machine_name && machine.machine_number) return `${machine.machine_name} (${machine.machine_number})`;
        return machine.machine_name || machine.machine_number || String(machine.machine_id || '');
    }, [t]);

    const machineStatusOption = useMemo(() => ({
        machine_id: '__MACHINE_LIST_STATUS__',
        machine_name: machineListError
            ? 'Unable to load machines. Please try again.'
            : machineSearch.trim()
                ? t('GENERAL.NO_MATCHES_FOUND')
                : t('LABELS.NO_MACHINES_FOUND'),
        disabled: true,
    }), [machineListError, machineSearch, t]);

    // Prepare machine options with "All machines" option
    const machineOptions = useMemo(() => {
        const rows = machines.length ? [...machines] : [machineStatusOption];
        if (
            selectedMachine?.machine_id &&
            selectedMachine.machine_id !== 'ALL' &&
            !rows.some((machine) => String(machine?.machine_id) === String(selectedMachine.machine_id))
        ) {
            rows.unshift(selectedMachine);
        }
        return [allMachinesOption, ...rows];
    }, [allMachinesOption, machineStatusOption, machines, selectedMachine]);

    // Get machine object from documents (PRIORITY SOURCE)
    const getMachineFromDocuments = useCallback((machineId) => {
        if (!machineId || !documents.length) return null;

        const machineDocs = documents.filter(doc =>
            doc.assigned_machines?.includes(String(machineId))
        );

        for (const doc of machineDocs) {
            const machineDetail = doc.machine_details?.find(m => m.machine_id === machineId);
            if (machineDetail) {
                return {
                    machine_id: machineId,
                    machine_name: machineDetail.machine_name || '',
                    machine_number: machineDetail.machine_number || ''
                };
            }
        }
        return null;
    }, [documents]);

    const getMachineFromDocumentsByNumber = useCallback((machineNumber) => {
        if (!machineNumber || !documents.length) return null;

        for (const doc of documents) {
            if (doc.machine_details) {
                const machineDetail = doc.machine_details.find(
                    m => String(m.machine_number) === String(machineNumber)
                );
                if (machineDetail) {
                    return {
                        machine_id: machineDetail.machine_id,
                        machine_name: machineDetail.machine_name || '',
                        machine_number: machineDetail.machine_number || ''
                    };
                }
            }
        }
        return null;
    }, [documents]);

    // Handle regular document file click (if onFileClick prop exists)
    const handleDocumentFileClick = useCallback((filePath, fileName) => {
        if (onFileClick) {
            onFileClick(filePath, fileName);
        } else {
            setSelectedFilePath(filePath);
            setSelectedFileName(fileName || filePath.split('/').pop());
            setModalOpen(true);
        }
    }, [onFileClick]);

    const isRealMachineId = useCallback((machine) => {
        if (!machine?.machine_id) return false;
        const idStr = String(machine.machine_id);
        if (idStr.toUpperCase() === 'ALL') return false;
        if (idStr.includes('-')) return false; // machine_number format like "21686-3001"
        if (machine.machine_number && idStr === String(machine.machine_number)) return false; // id same as number = not resolved yet
        return /^\d+$/.test(idStr); // must be purely numeric large integer
    }, []);

    const withMachineRequestDedupe = useCallback(async (key, requestFn) => {
        if (!key) return null;
        if (completedMachineRequestsRef.current.has(key)) return null;
        if (inFlightMachineRequestsRef.current.has(key)) return null;
        inFlightMachineRequestsRef.current.add(key);
        try {
            const result = await requestFn();
            if (result) completedMachineRequestsRef.current.add(key);
            return result;
        } finally {
            inFlightMachineRequestsRef.current.delete(key);
        }
    }, []);

    useEffect(() => {
        const fetchMachineByNumber = async () => {
            if (!selectedMachine?.machine_number || !selectedSiteNumber) return;
            // Avoid overlapping API calls with id-based resolver.
            if (isRealMachineId(selectedMachine)) return;

            const machineNumber = selectedMachine.machine_number;

            const fromDocs = getMachineFromDocumentsByNumber(machineNumber);
            if (fromDocs) { onMachineChange?.(fromDocs); return; }

            const fromList = machines.find(
                m => String(m.machine_number) === String(machineNumber)
            );
            if (fromList) { onMachineChange?.(fromList); return; }

            try {
                const requestKey = `${selectedSiteNumber}|number|${String(machineNumber)}`;
                const resp = await withMachineRequestDedupe(requestKey, () => getMachinesWithModels({
                    site_number: selectedSiteNumber,
                    page: 1,
                    limit: 50,
                    searchTerm: String(machineNumber),
                    sortBy: '',
                    hasDoc: false,          // CRITICAL FIX: was true — machine may have no docs
                    includeModelData: false,
                    includeBomCount: false,
                    includeGlobalProductCode: false,
                    includeSyncStatus: false,
                    includeTotalCount: false,
                }));
                if (!resp) return;
                const machineData = (resp?.data || []).find(
                    m => String(m.machine_number) === String(machineNumber)
                );
                if (machineData) {
                    setMachines(prev => {
                        const map = new Map(prev.map(m => [m.machine_id, m]));
                        map.set(machineData.machine_id, machineData);
                        return Array.from(map.values());
                    });
                    onMachineChange?.({
                        machine_id: machineData.machine_id,
                        machine_name: machineData.machine_name || '',
                        machine_number: machineData.machine_number || ''
                    });
                }
            } catch (err) {
                console.error('SIDEBAR: Error fetching machine by number:', err);
            }
        };
        fetchMachineByNumber();
    }, [
        selectedMachine?.machine_number,
        selectedMachine?.machine_id,
        selectedMachine?.machine_name,
        selectedSiteNumber,
        machines,
        documents,
        isRealMachineId,
        withMachineRequestDedupe,
        getMachineFromDocumentsByNumber,
        onMachineChange
    ]);

    const enrichedSelectedMachine = useMemo(() => {
        if (!selectedMachine) return null;
        if (selectedMachine.machine_id === 'ALL') return allMachinesOption;

        // FIX: Only return early if machine_name is known — machine_number alone is not enough
        if (selectedMachine.machine_name) return selectedMachine;

        const fromDocs = getMachineFromDocuments(selectedMachine.machine_id);
        if (fromDocs) return fromDocs;

        const fromList = machineOptions.find(m => m.machine_id === selectedMachine.machine_id);
        if (fromList?.machine_name) return fromList;

        return selectedMachine;
    }, [selectedMachine, machineOptions, getMachineFromDocuments, allMachinesOption]);

    useEffect(() => {
        if (!selectedMachine) return;
        if (
            enrichedSelectedMachine &&
            enrichedSelectedMachine.machine_id === selectedMachine.machine_id &&
            (
                enrichedSelectedMachine.machine_name !== selectedMachine.machine_name ||
                enrichedSelectedMachine.machine_number !== selectedMachine.machine_number
            )
        ) {
            onMachineChange?.(enrichedSelectedMachine);
        }
    }, [enrichedSelectedMachine, selectedMachine, onMachineChange]);

    const favoriteDocuments = useMemo(() => {
        if (favoriteDocumentsWithTimestamps.length > 0) return favoriteDocumentsWithTimestamps;
        if (favorites.length > 0 && typeof favorites[0] === 'number') {
            return documents.filter((doc) => favorites.includes(doc.id));
        }
        return favorites;
    }, [documents, favorites, favoriteDocumentsWithTimestamps]);

    const fetchMachineResults = useCallback(async (page = 1, q = '') => {
        const siteNumber = selectedSiteNumber;
        if (!siteNumber) {
            machineSearchRequestIdRef.current += 1;
            activeMachineListRequestRef.current = null;
            setMachines([]);
            setMachinePage(1);
            setMachineHasMore(false);
            setMachinesLoading(false);
            setMachineListError(false);
            return;
        }

        const normalizedSearch = String(q || '').trim();
        const requestKey = `${siteNumber}|${page}|${normalizedSearch.toLowerCase()}|documents-only`;
        const activeRequest = activeMachineListRequestRef.current;
        if (
            activeRequest?.key === requestKey &&
            activeRequest.requestId === machineSearchRequestIdRef.current
        ) return;

        const requestId = ++machineSearchRequestIdRef.current;
        activeMachineListRequestRef.current = { key: requestKey, requestId };

        try {
            setMachinesLoading(true);
            setMachineListError(false);

            const payload = {
                site_number: siteNumber,
                page: page,
                limit: 50,
                searchTerm: normalizedSearch,
                sortBy: '',
                hasDoc: true,
                includeModelData: false,
                includeBomCount: false,
                includeGlobalProductCode: false,
                includeSyncStatus: false,
                includeTotalCount: false,
            };




            const resp = await getMachinesWithModels(payload);
            if (requestId !== machineSearchRequestIdRef.current) return;
            setMachineListError(false);
            if (!resp || resp.error || resp.success === false) {
                throw new Error(resp?.message || resp?.error || 'Machine list request failed');
            }


            const rows = Array.isArray(resp?.data) ? resp.data : [];
            const total = resp?.pagination?.totalItems || 0;
            const trimmedSearchTerm = String(q || "").trim();
            const resultCount = Number(total || rows.length) || 0;

            if (page === 1 && trimmedSearchTerm) {
                const trackingKey = `${trimmedSearchTerm.toLowerCase()}|${siteNumber}|documentation_machine_filter|${resultCount}`;

                if (lastTrackedMachineSearchRef.current !== trackingKey) {
                    lastTrackedMachineSearchRef.current = trackingKey;

                    trackEvent("search_performed", {
                        search_term: trimmedSearchTerm,
                        search_source: "documentation_machine_filter",
                        page_name: "Documentation",
                        page_path: pagePathRef.current,
                        result_count: resultCount,
                        has_results: resultCount > 0,
                        no_results: resultCount === 0,
                        site_number: siteNumber,
                    });
                }
            }

            setMachines((prev) => {
                if (page === 1) return rows;
                const map = new Map(prev.map((m) => [m.machine_id, m]));
                rows.forEach((machine) => { if (machine?.machine_id) map.set(machine.machine_id, machine); });
                return Array.from(map.values());
            });
            setMachineHasMore(page * 50 < total);
            setMachinePage(page);
        } catch (err) {
            if (requestId !== machineSearchRequestIdRef.current) return;
            console.error('SIDEBAR: Error:', err);
            setMachineListError(true);
            setMachines([]); setMachineHasMore(false);
        } finally {
            if (requestId === machineSearchRequestIdRef.current) {
                activeMachineListRequestRef.current = null;
                setMachinesLoading(false);
            }
        }
    }, [selectedSiteNumber]);

    useEffect(() => {
        const siteChanged = previousSiteNumberRef.current !== selectedSiteNumber;
        previousSiteNumberRef.current = selectedSiteNumber;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
        setMachineSearch('');
        setMachineInputValue(getMachineDisplayName(selectedMachine));
        if (siteChanged) setMachines([]);
        fetchMachineResults(1, '');
    }, [selectedSiteNumber, selectedMachine?.machine_id, selectedMachine?.machine_name, selectedMachine?.machine_number, getMachineDisplayName, fetchMachineResults]);

    useEffect(() => {
        const fetchSelectedMachine = async () => {
            if (!selectedMachine?.machine_id || !selectedSiteNumber) return;
            if (!isRealMachineId(selectedMachine)) return;
            if (selectedMachine.machine_name && isRealMachineId(selectedMachine)) return;

            const fromDocs = getMachineFromDocuments(selectedMachine.machine_id);
            if (fromDocs) { onMachineChange?.(fromDocs); return; }

            const exists = machines.some(m => m.machine_id === selectedMachine.machine_id);
            if (exists) {
                const fromList = machines.find(m => m.machine_id === selectedMachine.machine_id);
                if (fromList?.machine_name) { onMachineChange?.(fromList); }
                return;
            }

            try {
                const requestKey = `${selectedSiteNumber}|id|${String(selectedMachine.machine_id)}`;
                const resp = await withMachineRequestDedupe(requestKey, () => getMachinesWithModels({
                    site_number: selectedSiteNumber,
                    page: 1,
                    limit: 1,
                    searchTerm: String(selectedMachine.machine_id),
                    sortBy: '',
                    hasDoc: false,          // CRITICAL FIX: was true — machine may have no docs
                    includeModelData: false,
                    includeBomCount: false,
                    includeGlobalProductCode: false,
                    includeSyncStatus: false,
                    includeTotalCount: false,
                }));
                if (!resp) return;
                const machineData = (resp?.data || [])[0];
                if (machineData && machineData.machine_id === selectedMachine.machine_id) {
                    setMachines(prev => {
                        const map = new Map(prev.map(m => [m.machine_id, m]));
                        map.set(machineData.machine_id, machineData);
                        return Array.from(map.values());
                    });
                    onMachineChange?.({
                        machine_id: machineData.machine_id,
                        machine_name: machineData.machine_name || '',
                        machine_number: machineData.machine_number || ''
                    });
                }
            } catch (err) {
                console.error('SIDEBAR: Error fetching selected machine:', err);
            }
        };
        fetchSelectedMachine();
    }, [
        selectedMachine?.machine_id,
        selectedMachine?.machine_name,
        selectedSiteNumber,
        machines,
        documents,
        isRealMachineId,
        withMachineRequestDedupe,
        getMachineFromDocuments,
        onMachineChange
    ]);

    const handleScroll = useCallback((e) => {
        const listboxNode = e.currentTarget;
        if (
            listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10 &&
            !machinesLoading && machineHasMore
        ) {
            fetchMachineResults(machinePage + 1, machineSearch);
        }
    }, [machinesLoading, machineHasMore, machinePage, machineSearch, fetchMachineResults]);

    const handleMachineChange = useCallback((event, newValue) => {
        highlightedMachineOptionRef.current = null;
        if (newValue?.disabled) return;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
        setMachineSearch('');
        setMachineInputValue(newValue ? (
            newValue.machine_id === 'ALL'
                ? getMachineDisplayName(allMachinesOption)
                : getMachineDisplayName(newValue)
        ) : '');
        if (newValue === null) { onMachineChange?.(null); return; }
        if (newValue?.machine_id === 'ALL') { onMachineChange?.(allMachinesOption); return; }
        onMachineChange?.(newValue);
    }, [getMachineDisplayName, onMachineChange, allMachinesOption]);

    const handleInputChange = useCallback((event, newInputValue, reason) => {
        setMachineInputValue(newInputValue);
        if (reason === 'input') {
            setMachineSearch(newInputValue);
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            const timer = setTimeout(() => {
                debounceTimerRef.current = null;
                fetchMachineResults(1, newInputValue || '');
            }, 250);
            debounceTimerRef.current = timer;
        } else if (reason === 'clear' || reason === 'reset') {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
            setMachineSearch('');
            highlightedMachineOptionRef.current = null;
            if (reason === 'clear' && !selectedMachine) fetchMachineResults(1, '');
        }
    }, [fetchMachineResults, selectedMachine]);

    const handleOpen = useCallback((event) => {
        // Match the site selector: focusing/clicking opens a fresh shortlist,
        // while the keystroke that opens the popup remains part of the query.
        if (event?.type === 'change') return;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
        setMachineSearch('');
        setMachineInputValue('');
        highlightedMachineOptionRef.current = null;
        fetchMachineResults(1, '');
    }, [fetchMachineResults]);

    const handleBlur = useCallback(() => {
        if (machineSearch.trim() !== '') {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
            setMachineSearch('');
            setMachineInputValue(selectedMachine ? getMachineDisplayName(selectedMachine) : '');
            fetchMachineResults(1, '');
        }
    }, [machineSearch, selectedMachine, getMachineDisplayName, fetchMachineResults]);

    useEffect(() => {
        return () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current); };
    }, []);

    const handleFavoritesClick = useCallback(() => {
        onToggleFavorites?.(!showOnlyFavorites);
    }, [showOnlyFavorites, onToggleFavorites]);

    const handleRecentClick = useCallback(() => {
        onToggleRecent?.(!showOnlyRecent);
    }, [showOnlyRecent, onToggleRecent]);

    const handleRemoveFromFavorites = useCallback(async (docId) => {
        try {
            if (onFavoriteRemoved) onFavoriteRemoved(docId);
            if (showOnlyFavorites && favoriteDocuments.length === 1) onToggleFavorites?.(false);
            await removeFavorite(docId, selectedSiteId);
        } catch (error) {
            console.error('Error removing favorite:', error);
        }
    }, [onFavoriteRemoved, selectedSiteId, showOnlyFavorites, favoriteDocuments.length, onToggleFavorites]);

    useEffect(() => {
        if (showOnlyFavorites && favoriteDocuments.length === 0) onToggleFavorites?.(false);
    }, [favoriteDocuments.length, showOnlyFavorites, onToggleFavorites]);

    return (
        <Box className="documentation-sidebar-wrapper">

            {/* ── Select Machine Card ── */}
            <Box
                className={`sidebar-card ${selectedMachine ? 'sidebar-card-active' : ''}`}
                sx={{
                    borderRadius: '8px', p: 2, transition: 'all 0.2s ease-in-out',
                    ...(selectedMachine && {
                        border: `1px solid ${themeColors.blue.main}`,
                        backgroundColor: 'transparent',
                    }),
                }}
            >
                <Box className="sidebar-card-header">
                    <Button color="primary" variant="roundedGreyBgSquareContained" sx={{ pointerEvents: "none" }}>
                        <ViewListIcon sx={{ fontSize: '20px' }} />
                    </Button>
                    <Typography variant="h6" className="sidebar-card-title">
                        {t('LABELS.SELECT_MACHINE')}
                    </Typography>
                </Box>

                <Autocomplete
                    size="small"
                    options={machineOptions}
                    disablePortal
                    filterOptions={(options) => {
                        if (machinesLoading && machines.length === 0) return [];
                        return machineSearch.trim()
                            ? options.filter((machine) => machine?.machine_id !== 'ALL')
                            : options;
                    }}
                    getOptionLabel={(machine) => {
                        if (!machine) return '';
                        if (machine.machine_id === 'ALL') return t('LABELS.ALL_MACHINES');
                        if (machine.machine_name && machine.machine_number)
                            return `${machine.machine_name} (${machine.machine_number})`;
                        if (machine.machine_name) return String(machine.machine_name);
                        if (machine.machine_number) return String(machine.machine_number);
                        return String(machine.machine_id || '');
                    }}
                    isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return option === value;
                        return String(option.machine_id) === String(value.machine_id);
                    }}
                    getOptionDisabled={(option) => Boolean(option?.disabled)}
                    value={enrichedSelectedMachine}
                    inputValue={machineInputValue}
                    onChange={handleMachineChange}
                    onHighlightChange={(event, option) => {
                        highlightedMachineOptionRef.current = option;
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                        handleInputChange(event, newInputValue, reason);
                    }}
                    onBlur={handleBlur}
                    onOpen={handleOpen}
                    openOnFocus
                    loading={machinesLoading}
                    loadingText={t('LABELS.LOADING')}
                    noOptionsText={machineListError
                        ? 'Unable to load machines. Please try again.'
                        : t('GENERAL.NO_MATCHES_FOUND')}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            inputProps={{
                                ...params.inputProps,
                                onKeyDown: (event) => {
                                    if (
                                        event.key === 'Enter' &&
                                        !machineSearch.trim() &&
                                        !highlightedMachineOptionRef.current
                                    ) {
                                        event.preventDefault();
                                        event.defaultMuiPrevented = true;
                                        return;
                                    }
                                    params.inputProps.onKeyDown?.(event);
                                },
                            }}
                            placeholder={
                                enrichedSelectedMachine
                                    ? getMachineDisplayName(enrichedSelectedMachine)
                                    : t('LABELS.ALL_MACHINES')
                            }
                            variant="outlined"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {machinesLoading ? (
                                            <CircularProgress color="inherit" size={16} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontSize: '0.875rem', borderRadius: '6px',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: selectedMachine ? themeColors.blue.main : '#e0e0e0',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: selectedMachine ? themeColors.blue.main : '#e0e0e0',
                                    },
                                },
                            }}
                        />
                    )}
                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        if (option?.disabled) {
                            return (
                                <li key={key ?? option.machine_id} {...optionProps}>
                                    <Typography
                                        role="status"
                                        variant="body2"
                                        sx={{ width: '100%', py: 0.75, color: 'text.secondary', fontStyle: 'italic' }}
                                    >
                                        {option.machine_name}
                                    </Typography>
                                </li>
                            );
                        }
                        return (
                            <li key={key ?? option?.machine_id ?? 'all'} {...optionProps}>
                                <Box sx={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                                    rowGap: 0, width: '100%', px: 0.5, py: 0.5
                                }}>
                                    <Typography variant="body1" sx={{
                                        fontSize: '13px',
                                        fontWeight: option.machine_id === selectedMachine?.machine_id ? 'bold' : '500',
                                        color: option.machine_id === selectedMachine?.machine_id
                                            ? themeColors.blue.main : 'inherit'
                                    }}>
                                        {option?.machine_name || option?.machine_number || String(option?.machine_id || '')}
                                    </Typography>
                                    {option?.machine_number && option.machine_number !== option.machine_id && (
                                        <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                            <b>{t('UI_TEXT.MACHINE')}</b> {option.machine_number}
                                        </Typography>
                                    )}
                                    {option?.machine_id && !option.machine_number && option.machine_id !== null && option.machine_id !== 'ALL' && (
                                        <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                            <b>{t('UI_TEXT.MACHINE')}</b> {option.machine_id}
                                        </Typography>
                                    )}
                                </Box>
                            </li>
                        );
                    }}
                    noOptionsText={
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                            {machinesLoading ? t('LABELS.LOADING') : t('LABELS.NO_MACHINES_FOUND')}
                        </Typography>
                    }
                />
            </Box>

            {/* ── Favorites Card ── */}
            <Box className={`sidebar-card ${showOnlyFavorites ? 'sidebar-card-active' : ''}`}>
                <Box className="sidebar-card-header-with-count">
                    <Box className="sidebar-card-header">
                        <Button color="primary" variant="roundedGreyBgSquareContained" sx={{ pointerEvents: "none" }}>
                            <StarIcon sx={{ fontSize: '20px' }} />
                        </Button>
                        <Typography variant="h6" className="sidebar-card-title">
                            {t('LABELS.FAVORITES')} {favoriteDocuments.length > 0 && `(${favoriteDocuments.length})`}
                        </Typography>
                    </Box>
                    {favoriteDocuments.length > 0 && (
                        <Typography
                            className={`sidebar-filter-link ${showOnlyFavorites ? 'sidebar-filter-link-active' : ''}`}
                            onClick={handleFavoritesClick}
                        >
                            {showOnlyFavorites ? t('LABELS.HIDE') : t('LABELS.VIEW_ALL')}
                        </Typography>
                    )}
                </Box>
                {favoriteDocuments.length > 0 ? (
                    <List dense className="sidebar-list">
                        {favoriteDocuments.slice(0, 5).map((doc) => {
                            const machineDetail = doc.machine_details?.[0] || {};
                            const displayMachineNumber = machineDetail.machine_number || doc.machine_number;
                            const displayMachineId = machineDetail.machine_id || doc.machine_id;
                            return (
                                <ListItem
                                    key={doc.id}
                                    className="sidebar-list-item"
                                    sx={{
                                        paddingRight: '8px', display: 'flex', alignItems: 'center',
                                        '&:hover .MuiIconButton-root': { opacity: 1 },
                                    }}
                                    secondaryAction={
                                        <Tooltip title={t('LABELS.REMOVE_FROM_FAVORITES')} placement="left" arrow>
                                            <IconButton
                                                edge="end" size="small"
                                                onClick={() => handleRemoveFromFavorites(doc.id)}
                                                aria-label={t('LABELS.REMOVE_FROM_FAVORITES')}
                                                color="primary"
                                                sx={{
                                                    opacity: 0, transition: 'opacity 0.2s ease',
                                                    color: 'primary', position: 'absolute',
                                                    left: 'calc(100% - 10px)', top: '50%',
                                                    transform: 'translateY(-50%)',
                                                }}
                                            >
                                                <CloseIcon sx={{ fontSize: '20px' }} />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                >
                                    <ListItemIcon className="sidebar-list-icon" sx={{ minWidth: '36px' }}>
                                        <Box component="span" className="sidebar-icon-box">
                                            <DocCardImage filename={doc.name} path={doc.attachment} fileExtension={doc.fileExtension} />
                                        </Box>
                                    </ListItemIcon>
                                    <Tooltip title={doc.name} placement="top" arrow enterDelay={500}>
                                        <ListItemText
                                            primary={doc.name}
                                            secondary={
                                                <>
                                                    {displayMachineNumber && displayMachineNumber !== displayMachineId && (
                                                        <Typography component="span" variant="body2" sx={{ fontSize: '0.7rem', display: 'block', color: 'text.primary', fontWeight: 500 }}>
                                                            <Box component="span" sx={{ color: 'text.secondary' }}>{t('UI_TEXT.MACHINE')}</Box>
                                                            {' '}{displayMachineNumber}
                                                        </Typography>
                                                    )}
                                                    {displayMachineId && !displayMachineNumber && displayMachineId !== null && (
                                                        <Typography component="span" variant="body2" sx={{ fontSize: '0.7rem', display: 'block', color: 'text.primary', fontWeight: 500 }}>
                                                            <Box component="span" sx={{ color: 'text.secondary' }}>{t('UI_TEXT.MACHINE')}</Box>
                                                            {' '}{displayMachineId}
                                                        </Typography>
                                                    )}
                                                    <Typography component="span" variant="caption" sx={{ fontSize: '0.7rem', display: 'block', color: 'text.secondary' }}>
                                                        {getRelativeTime(doc.favorited_at)}
                                                    </Typography>
                                                </>
                                            }
                                            primaryTypographyProps={{
                                                variant: 'body2', fontSize: '0.813rem', noWrap: true,
                                                color: 'text.primary', fontWeight: 500,
                                                sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                                            }}
                                            secondaryTypographyProps={{ variant: 'caption', component: 'div' }}
                                            sx={{ overflow: 'hidden', minWidth: 0, flex: 1, paddingRight: '32px' }}
                                        />
                                    </Tooltip>
                                </ListItem>
                            );
                        })}
                    </List>
                ) : (
                    <Typography className="sidebar-empty-text">{t('LABELS.NO_FAVORITES')}</Typography>
                )}
            </Box>

            {/* ── Recently Viewed Card ── */}
            <Box className={`sidebar-card ${showOnlyRecent ? 'sidebar-card-active' : ''}`}>
                <Box className="sidebar-card-header-with-count">
                    <Box className="sidebar-card-header">
                        <Button color="primary" variant="roundedGreyBgSquareContained" sx={{ pointerEvents: "none" }}>
                            <HistoryIcon sx={{ fontSize: '20px' }} />
                        </Button>
                        <Typography variant="h6" className="sidebar-card-title">
                            {t('LABELS.RECENTLY_VIEWED')} {recentDocuments.length > 0 && `(${recentDocuments.length})`}
                        </Typography>
                    </Box>
                    {recentDocuments.length > 0 && (
                        <Typography
                            className={`sidebar-filter-link ${showOnlyRecent ? 'sidebar-filter-link-active' : ''}`}
                            onClick={handleRecentClick}
                        >
                            {showOnlyRecent ? t('LABELS.HIDE') : t('LABELS.VIEW_ALL')}
                        </Typography>
                    )}
                </Box>
                {recentDocuments.length > 0 ? (
                    <List dense className="sidebar-list">
                        {recentDocuments.slice(0, 5).map((doc) => {
                            const machineDetail = doc.machine_details?.[0] || {};
                            const displayMachineNumber = machineDetail.machine_number || doc.machine_number;
                            const displayMachineId = machineDetail.machine_id || doc.machine_id;
                            return (
                                <ListItem key={doc.id} className="sidebar-list-item" sx={{ paddingRight: '16px', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ListItemIcon className="sidebar-list-icon" sx={{ minWidth: '36px' }}>
                                        <Box component="span" className="sidebar-icon-box">
                                            <DocCardImage filename={doc.name} path={doc.attachment} />
                                        </Box>
                                    </ListItemIcon>
                                    <Tooltip title={doc.name} placement="top" arrow enterDelay={500}>
                                        <ListItemText
                                            primary={doc.name}
                                            secondary={
                                                <>
                                                    {displayMachineNumber && displayMachineNumber !== displayMachineId && (
                                                        <Typography component="span" variant="body2" sx={{ fontSize: '0.7rem', display: 'block', color: 'text.primary', fontWeight: 500 }}>
                                                            <Box component="span" sx={{ color: 'text.secondary' }}>{t('UI_TEXT.MACHINE')}</Box>
                                                            {' '}{displayMachineNumber}
                                                        </Typography>
                                                    )}
                                                    {displayMachineId && !displayMachineNumber && displayMachineId !== null && (
                                                        <Typography component="span" variant="body2" sx={{ fontSize: '0.7rem', display: 'block', color: 'text.primary', fontWeight: 500 }}>
                                                            <Box component="span" sx={{ color: 'text.secondary' }}>{t('UI_TEXT.MACHINE')}</Box>
                                                            {' '}{displayMachineId}
                                                        </Typography>
                                                    )}
                                                    <Typography component="span" variant="caption" sx={{ fontSize: '0.7rem', display: 'block', color: 'text.secondary' }}>
                                                        {doc.viewedTime}
                                                    </Typography>
                                                </>
                                            }
                                            primaryTypographyProps={{
                                                variant: 'body2', fontSize: '0.813rem', noWrap: true,
                                                color: 'text.primary', fontWeight: 500,
                                                sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                                            }}
                                            secondaryTypographyProps={{ variant: 'caption', component: 'div' }}
                                            sx={{ overflow: 'hidden', minWidth: 0, flex: 1 }}
                                        />
                                    </Tooltip>
                                </ListItem>
                            );
                        })}
                    </List>
                ) : (
                    <Typography className="sidebar-empty-text">{t('LABELS.NO_RECENT_DOCUMENTS')}</Typography>
                )}
            </Box>

            {/* ── Historical Documentation Card – navigates to full-page view ── */}
            <Box
                className="sidebar-card"
                onClick={() => navigate('/documentation/historical-content')}
                sx={{
                    cursor: 'pointer',
                    p: 1, // Further reduced padding
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }
                }}
            >
                <Box className="sidebar-card-header" sx={{
                    alignItems: 'center',
                    py: 0,
                    my: 0 // Remove any margin
                }}>
                    <Button
                        color="primary"
                        variant="roundedGreyBgSquareContained"
                        onClick={(e) => e.stopPropagation()}
                        tabIndex={-1}
                        sx={{
                            minWidth: '28px',
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            p: 0,
                            pointerEvents: "none"
                        }}
                    >
                        <FolderIcon sx={{ fontSize: '16px' }} />
                    </Button>
                    <Typography variant="h6" className="sidebar-card-title" sx={{
                        flex: 1,
                        ml: 1,
                        fontSize: '1rem',
                        lineHeight: 1.1,
                        fontWeight: 600 // Increased font weight
                    }}>
                        {t('DOCUMENT_DASHBOARD.HISTORICAL_DOCUMENTATION')}
                    </Typography>
                </Box>
            </Box>

            {/* File Preview Modal (fallback, normally not used from sidebar now) */}
            <DocumentViewModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedFilePath(null);
                    setSelectedFileName(null);
                }}
                filePath={selectedFilePath}
                fileName={selectedFileName}
            />
        </Box>
    );
};

export default DocumentationSidebar;
